import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  FiHome,
  FiPackage,
  FiTruck,
  FiUsers,
  FiDollarSign,
  FiPieChart,
  FiChevronDown,
  FiChevronRight,
  FiLogOut,
  FiBookOpen
} from 'react-icons/fi';
import { BsCash } from 'react-icons/bs';
import { BiReceipt } from 'react-icons/bi';
import './sidebar.css';
import axios from 'axios';
import { clearSession } from '../auth';

import logo from '../mainLogo.png';

const Sidebar = () => {
  const location = useLocation();
  const navigate = useNavigate();

  // Automatically expand the correct menu based on current route
  const [expandedItems, setExpandedItems] = useState({
    inventory:
      location.pathname.startsWith('/inventory') ||
      location.pathname.startsWith('/categories'),
    sales: location.pathname.startsWith('/sales')
  });

  const toggleExpand = (item) => {
    setExpandedItems((prev) => ({
      ...prev,
      [item]: !prev[item]
    }));
  };

  const handleLogout = async () => {
    try {
      await axios.post('/users/logout');
      clearSession();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
      clearSession();
      navigate('/login');
    }
  };

  const normalizePath = (path) => path.replace(/\/+$/, '');

  const isActive = (path) => {
    const current = normalizePath(location.pathname);
    const target = normalizePath(path);
    return current === target || current.startsWith(target + '/');
  };

  const isExactActive = (path) => {
    return normalizePath(location.pathname) === normalizePath(path);
  };

  return (
    <div className="sidebar">
      {/* Header */}
      <div className="sidebar-header">
        <img
          src={logo}
          alt="Inventory Manager Logo"
          className="sidebar-logo"
        />

        <h1 className="sidebar-title">
          Inventory Manager
        </h1>
      </div>

      {/* Navigation */}
      <nav className="sidebar-nav">
        <ul>
          {/* Dashboard */}
          <li>
            <Link
              to="/dashboard"
              className={`flex items-center ${
                isActive('/dashboard') ? 'bg-gray-700' : ''
              }`}
            >
              <FiHome className="icon" />
              <span>Dashboard</span>
            </Link>
          </li>

          {/* Inventory */}
          <li>
            <div
              className={`flex items-center justify-between cursor-pointer ${
                isActive('/inventory') || isActive('/categories')
                  ? 'bg-gray-700'
                  : ''
              }`}
              onClick={() => toggleExpand('inventory')}
            >
              <div className="flex items-center">
                <FiPackage className="icon" />
                <span>Inventory</span>
              </div>

              {expandedItems.inventory ? (
                <FiChevronDown />
              ) : (
                <FiChevronRight />
              )}
            </div>

            {expandedItems.inventory && (
              <ul>
                <li>
                  <Link
                    to="/inventory/add"
                    className={isActive('/inventory/add') ? 'bg-gray-600' : ''}
                  >
                    Add Items
                  </Link>
                </li>

                <li>
                  <Link
                    to="/inventory/viewInventory"
                    className={
                      isActive('/inventory/viewInventory')
                        ? 'bg-gray-600'
                        : ''
                    }
                  >
                    View Inventory
                  </Link>
                </li>

                <li>
                  <Link
                    to="/categories"
                    className={isActive('/categories') ? 'bg-gray-600' : ''}
                  >
                    View Category
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {/* Suppliers */}
          <li>
            <Link
              to="/suppliers"
              className={`flex items-center ${
                isActive('/suppliers') ? 'bg-gray-700' : ''
              }`}
            >
              <FiTruck className="icon" />
              <span>Suppliers</span>
            </Link>
          </li>

          {/* Retailers */}
          <li>
            <Link
              to="/retailers"
              className={`flex items-center ${
                isActive('/retailers') ? 'bg-gray-700' : ''
              }`}
            >
              <FiUsers className="icon" />
              <span>Retailers</span>
            </Link>
          </li>

          {/* Sales */}
          <li>
            <div
              className={`flex items-center justify-between cursor-pointer ${
                isActive('/sales') ? 'bg-gray-700' : ''
              }`}
              onClick={() => toggleExpand('sales')}
            >
              <div className="flex items-center">
                <FiDollarSign className="icon" />
                <span>Sales</span>
              </div>

              {expandedItems.sales ? (
                <FiChevronDown />
              ) : (
                <FiChevronRight />
              )}
            </div>

            {expandedItems.sales && (
              <ul>
                 <li>
                  <Link
                    to="/sales/add"
                    className={isExactActive('/sales/add') ? 'bg-gray-600' : ''}
                  >
                    New Sales
                  </Link>
                </li>
                <li>
                  <Link
                    to="/sales"
                    className={isExactActive('/sales') ? 'bg-gray-600' : ''}
                  >
                    Sales List
                  </Link>
                </li>

                <li>
                  <Link
                    to="/sales/credit"
                    className={
                      isActive('/sales/credit') ? 'bg-gray-600' : ''
                    }
                  >
                    Credit
                  </Link>
                </li>
              </ul>
            )}
          </li>

          {/* Payment */}
          <li>
            <Link
              to="/payments/add"
              className={`flex items-center ${
                isActive('/payments') ? 'bg-gray-700' : ''
              }`}
            >
              <BsCash className="icon" />
              <span>Payment</span>
            </Link>
          </li>

          {/* Bank Balance */}
          <li>
            <Link
              to="/balance"
              className={`flex items-center ${
                isActive('/balance') ? 'bg-gray-700' : ''
              }`}
            >
              <BiReceipt className="icon" />
              <span>Bank Balance</span>
            </Link>
          </li>

          {/* Expenses */}
          <li>
            <Link
              to="/expenses"
              className={`flex items-center ${
                isActive('/expenses') ? 'bg-gray-700' : ''
              }`}
            >
              <BiReceipt className="icon" />
              <span>Expenses</span>
            </Link>
          </li>

          {/* Day Book */}
          <li>
            <Link
              to="/day-book"
              className={`flex items-center ${
                isActive('/day-book') ? 'bg-gray-700' : ''
              }`}
            >
              <FiBookOpen className="icon" />
              <span>Day Book</span>
            </Link>
          </li>

          {/* Reports */}
          <li>
            <Link
              to="/reports"
              className={`flex items-center ${
                isActive('/reports') ? 'bg-gray-700' : ''
              }`}
            >
              <FiPieChart className="icon" />
              <span>Reports</span>
            </Link>
          </li>
        </ul>
      </nav>

      {/* Logout */}
      <div className="logout-container">
        <button onClick={handleLogout} className="logout-button">
          <FiLogOut className="logout-icon" />
          <span>Logout</span>
        </button>
      </div>
    </div>
  );
};
export default Sidebar;